import { useState } from 'react'
import { useMutationState } from '@tanstack/react-query'
import { InputBase, MenuItem, Select, Stack, Typography } from '@mui/material'

import { fieldInputSx } from '@/apps/latest/shared/styles/inlineInput'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { FONT_WEIGHT, scaledFontSize } from '@/shared/styles/typography'

import {
  CARE_LEVELS,
  type HomecarePatient,
  type HomecarePatientInput,
  type HomecareRecipient,
  type RecipientKind,
} from './homecareApi'
import { formatBirthDate } from './printHomeVisitReport'
import { homecarePatientSaveKey, useUpdateHomecarePatient } from './useHomecare'

// 画面で直せる単位。保存の body のキーと同じ名前
type EditKey = 'care_level' | 'recipients'

export const RECIPIENT_KINDS: { key: RecipientKind; label: string }[] = [
  { key: 'physician', label: '処方医' },
  { key: 'care_manager', label: 'ケアマネ' },
  { key: 'other', label: 'その他' },
]

const EMPTY_RECIPIENT: HomecareRecipient = {
  kind: 'physician',
  facility: '',
  name: '',
}

// 宛先の行。最後の空の行が足す場所 (栄養の患者 ID と同じ形)。空にした行は保存で落ちる。
export const recipientRows = (
  recipients: HomecareRecipient[]
): HomecareRecipient[] => [...recipients, EMPTY_RECIPIENT]

export const filledRecipients = (
  rows: HomecareRecipient[]
): HomecareRecipient[] =>
  rows
    .map((row) => ({
      kind: row.kind,
      facility: row.facility.trim(),
      name: row.name.trim(),
    }))
    .filter((row) => row.facility || row.name)

export const labelSx = {
  flexShrink: 0,
  width: 88,
  fontSize: scaledFontSize('label'),
  color: colorTokens.fg.muted,
} as const

// 読むだけの値 (氏名・生年月日・患者番号)。入力の欄と高さをそろえる
const valueSx = {
  minHeight: 32,
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: scaledFontSize('bodySm'),
  color: colorTokens.fg.default,
} as const

const mutedValueSx = {
  ...valueSx,
  fontSize: scaledFontSize('label'),
  color: colorTokens.fg.muted,
} as const

export const sectionHeadingSx = {
  pt: 2,
  fontSize: scaledFontSize('label'),
  fontWeight: FONT_WEIGHT.medium,
  color: colorTokens.fg.muted,
} as const

interface Props {
  patient: HomecarePatient
}

// 在宅の患者の欄と宛先。氏名・生年月日・患者番号は既存の患者のもので読むだけ (正はレセコン)。
// 介護度は選んだ時点で、宛先は欄を離れた時点で保存する。直した値は、次に出す報告書の頭書きから効く
// (頭書きは出すときに組むので、出した紙は変わらない)。
export const HomecarePatientPanel = ({ patient }: Props) => {
  const save = useUpdateHomecarePatient(patient.id)

  const [careLevel, setCareLevel] = useState(patient.care_level)
  const [rows, setRows] = useState(recipientRows(patient.recipients))
  // 打っている途中 (まだ離れていない) の宛先と、保存がまだ終わっていない欄。
  // 保存のあとの取り直しが届いても、この 2 つは画面の値を残す (次の欄に打ち始めた字を消さない)。
  const [typing, setTyping] = useState(false)
  const saving = useMutationState({
    filters: {
      mutationKey: homecarePatientSaveKey(patient.id),
      status: 'pending',
    },
    select: (mutation) =>
      Object.keys((mutation.state.variables ?? {}) as HomecarePatientInput),
  }).flat()
  const [seen, setSeen] = useState(patient)
  if (seen !== patient) {
    const switched = seen.id !== patient.id
    const keep = (key: EditKey) =>
      !switched && ((key === 'recipients' && typing) || saving.includes(key))
    setSeen(patient)
    if (!keep('care_level')) setCareLevel(patient.care_level)
    if (!keep('recipients')) setRows(recipientRows(patient.recipients))
    if (switched) setTyping(false)
  }

  const commit = (input: HomecarePatientInput) => save.mutate(input)

  const commitCareLevel = (value: string) => {
    setCareLevel(value)
    if (value !== patient.care_level) commit({ care_level: value })
  }

  const commitRecipients = (next: HomecareRecipient[]) => {
    setTyping(false)
    const filled = filledRecipients(next)
    if (JSON.stringify(filled) === JSON.stringify(patient.recipients)) return
    commit({ recipients: filled })
  }

  const setRow = (index: number, change: Partial<HomecareRecipient>) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...change } : row
    )
    const last = next[next.length - 1]
    const grown = last.facility || last.name ? recipientRows(next) : next
    setRows(grown)
    return grown
  }

  return (
    <Stack spacing={0.75} sx={{ maxWidth: 640 }}>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography component="span" sx={labelSx}>
          氏名
        </Typography>
        <Typography component="span" sx={valueSx}>
          {patient.name}
        </Typography>
        <Typography component="span" sx={mutedValueSx}>
          {patient.name_kana}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography component="span" sx={labelSx}>
          生年月日
        </Typography>
        <Typography component="span" sx={valueSx}>
          {patient.birth_date ? formatBirthDate(patient.birth_date) : ''}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="baseline" spacing={1}>
        <Typography component="span" sx={labelSx}>
          患者番号
        </Typography>
        <Typography component="span" sx={valueSx}>
          {patient.patient_code}
        </Typography>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography component="span" sx={labelSx}>
          介護度
        </Typography>
        <Select
          value={careLevel}
          onChange={(e) => commitCareLevel(e.target.value)}
          displayEmpty
          input={
            <InputBase sx={{ ...fieldInputSx, flex: 'none', width: 200 }} />
          }
          SelectDisplayProps={{ 'aria-label': '介護度' }}
        >
          {CARE_LEVELS.map((level) => (
            <MenuItem key={level.value} value={level.value}>
              {level.label}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      <Typography component="h2" sx={sectionHeadingSx}>
        報告書の宛先
      </Typography>
      {rows.map((row, index) => (
        <Stack key={index} direction="row" alignItems="center" spacing={1}>
          <Select
            value={row.kind}
            onChange={(e) => {
              const next = setRow(index, {
                kind: e.target.value as RecipientKind,
              })
              commitRecipients(next)
            }}
            input={
              <InputBase sx={{ ...fieldInputSx, flex: 'none', width: 120 }} />
            }
            SelectDisplayProps={{ 'aria-label': `宛先 ${index + 1} の種類` }}
          >
            {RECIPIENT_KINDS.map((kind) => (
              <MenuItem key={kind.key} value={kind.key}>
                {kind.label}
              </MenuItem>
            ))}
          </Select>
          <InputBase
            value={row.facility}
            onChange={(e) => {
              setTyping(true)
              setRow(index, { facility: e.target.value })
            }}
            onBlur={() => commitRecipients(rows)}
            placeholder={row.kind === 'physician' ? '医療機関' : '事業所'}
            inputProps={{
              'aria-label': `宛先 ${index + 1} の医療機関・事業所`,
            }}
            sx={fieldInputSx}
          />
          <InputBase
            value={row.name}
            onChange={(e) => {
              setTyping(true)
              setRow(index, { name: e.target.value })
            }}
            onBlur={() => commitRecipients(rows)}
            placeholder="氏名"
            inputProps={{ 'aria-label': `宛先 ${index + 1} の氏名` }}
            sx={{ ...fieldInputSx, flex: 'none', width: 160 }}
          />
        </Stack>
      ))}
    </Stack>
  )
}
